import {
  DECODE_LAG_DWELL_MS,
  DECODE_LAG_MIN_GROWTH_MS,
  PRESENT_DEFICIT_RATIO,
  type DecodeLagEpisode,
} from './decodeLagContracts';

/**
 * Dwell-based decode-lag episode detector.
 *
 * Field data (webrtc-debug.ndjson) showed the Android failure: a decode backlog that grows
 * for seconds while the link stays clean. A single 1s sample can be a Safari blip (a 4fps
 * dip that clears next tick), so the episode only activates once the backlog holds past the
 * shared dwell AND the growth floor, and clears the moment the backlog resolves. The
 * detector reads the guest sampler's per-tick outputs; the episode rides the existing 1s
 * clientStats stream to the host (see guestCongestionMonitor).
 */
export class DecodeLagDetector {
  private active = false;
  private firstActiveAtMs: number | null = null;
  private readonly nowMs: () => number;

  constructor(options: { nowMs?: () => number } = {}) {
    this.nowMs = options.nowMs ?? (() => Date.now());
  }

    /** Observe one sampler tick. Returns the current episode state. */
  public observe(tick: {
    backlogFrames: number;
    playoutLatencyTrendMs: number;
    decodeFps: number;
    /** Absolute decoder depth this tick, when the engine can measure it. */
    standingBacklogFrames?: number;
    /** Per-frame decoder residency this tick, when the engine can measure it. */
    decodeMsPerFrame?: number;
    /**
     * What reached the glass this tick (rVFC presented fps). On engines that cannot measure
     * the frame-counter backlog (Safari), a sustained gap between decoded and presented is
     * the decode-side evidence this detector exists to find — frames die at the render
     * deadline instead of queueing in a measurable backlog.
     */
    presentedFps?: number;
  }): DecodeLagEpisode {
    const backlogMs = tick.playoutLatencyTrendMs;
    const frames = tick.backlogFrames;
    const isBacklogged = backlogMs >= DECODE_LAG_MIN_GROWTH_MS;
    const isGrowing = backlogMs >= DECODE_LAG_MIN_GROWTH_MS;

    // Presentation-deficit path: decoded well above presented, sustained past the same dwell.
    // Only consulted when the engine has no backlog evidence (frames counter absent) — on
    // Chromium both signals would describe the same episode and the backlog one wins.
    const isPresentDeficit =
      tick.standingBacklogFrames === undefined &&
      typeof tick.presentedFps === 'number' &&
      tick.decodeFps > 0 &&
      tick.presentedFps >= 0 &&
      tick.presentedFps < tick.decodeFps * PRESENT_DEFICIT_RATIO;

    const isEpisode = isBacklogged && isGrowing ? true : isPresentDeficit;
    const now = this.nowMs();

    if (isEpisode) {
      if (this.firstActiveAtMs === null) this.firstActiveAtMs = now;
      const dwelled = now - this.firstActiveAtMs >= DECODE_LAG_DWELL_MS;
      this.active = dwelled;
    } else {
      this.firstActiveAtMs = null;
      this.active = false;
    }

    this._current = {
      // A presentation-deficit episode carries zero counter backlog by construction; the
      // presented/decoded gap rides the dedicated field so the host never reads the two
      // shapes as the same signal.
      backlogFrames: isBacklogged ? frames : 0,
      playoutLatencyTrendMs: isBacklogged ? backlogMs : 0,
      decodeFps: tick.decodeFps,
      active: this.active,
      ...(tick.standingBacklogFrames !== undefined ? { standingBacklogFrames: tick.standingBacklogFrames } : {}),
      ...(tick.decodeMsPerFrame !== undefined ? { decodeMsPerFrame: tick.decodeMsPerFrame } : {}),
      ...(isPresentDeficit && tick.presentedFps !== undefined ? { presentedFps: tick.presentedFps } : {}),
    };
    return this._current;
  }

  /** Current episode, or null if no active episode. */
  public current(): DecodeLagEpisode | null {
    return this.active ? this._current : null;
  }

  private _current: DecodeLagEpisode | null = null;

  reset(): void {
    this.active = false;
    this.firstActiveAtMs = null;
    this._current = null;
  }
}
